(() => {
  if (customElements.get('maya-rug-categories')) return;
  const STYLE = 'filter.p.m.custom.rug_style';
  const SIZE = 'filter.p.m.custom.rug_size';
  // Canonical category IDs are independent of the storefront language.
  const definitions = {
    [STYLE]: ['traditional', 'tribal', 'transitional', 'contemporary'],
    [SIZE]: ['runner', 'small', 'medium', 'large', 'oversized']
  };
  const normal = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  function category(key, value, text) {
    const name = normal(value);
    return (definitions[key] || []).find(id => {
      const aliases = [text[id], ...(text[id + '_aliases'] || '').split('|')].filter(Boolean);
      return aliases.some(alias => {
        const label = normal(alias);
        return name === label || name.startsWith(label + ' - ') || name.startsWith(label + ' — ');
      });
    });
  }
  function displayText(value) {
    // Liquid's translation filter can HTML-escape text before JSON serialization.
    // Decode entity tokens only, then render with textContent. Never parse labels
    // as HTML or alter Shopify's functional filter values and URLs.
    const decoder = document.createElement('textarea');
    return String(value).replace(/&(?:#\d+|#x[\da-f]+|[a-z][a-z\d]+);/gi, entity => {
      decoder.innerHTML = entity;
      return decoder.value;
    });
  }
  function readConfig(node) {
    const config = JSON.parse(node.textContent);
    config.text = Object.fromEntries(Object.entries(config.text).map(([key, value]) => [key, displayText(value)]));
    config.filters.forEach(filter => {
      filter.label = displayText(filter.label);
      filter.values.forEach(item => { item.label = displayText(item.label); });
    });
    return config;
  }
  const catalogs = new Map();
  class MayaRugCategories extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this.querySelector('[data-maya-config]'));
      this.filters = this.config.filters;
      this.refresh = () => this.render();
      window.addEventListener('theme:navigation:productsUpdated', this.refresh);
      window.addEventListener('popstate', this.refresh);
      // Enhance the initial Liquid-rendered heading immediately. Network work
      // can add sibling choices or reconcile stale URLs without hiding content.
      this.render();
      this.start();
    }
    disconnectedCallback() {
      window.removeEventListener('theme:navigation:productsUpdated', this.refresh);
      window.removeEventListener('popstate', this.refresh);
    }
    async start() {
      const current = new URL(location.href);
      // Filtered collections can omit sibling values. Ask Shopify for this same
      // section without filters to get the complete localized category catalog.
      if ([...current.searchParams.keys()].some(key => key.startsWith('filter.'))) {
        const url = new URL(this.config.collectionUrl, location.origin);
        ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
          if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        });
        url.searchParams.set('section_id', this.config.sectionId);
        try {
          if (!catalogs.has(url.href)) catalogs.set(url.href, fetch(url.href, { credentials: 'same-origin' }).then(async response => {
            if (!response.ok) throw new Error('Category catalog request failed');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const config = html.querySelector('maya-rug-categories [data-maya-config]');
            if (!config) throw new Error('Category catalog missing');
            return readConfig(config);
          }));
          const baseline = await catalogs.get(url.href);
          if (!this.isConnected) return;
          this.filters = baseline.filters;
          if (await this.normalizeSelection()) return;
        } catch (error) {
          catalogs.delete(url.href);
          console.warn('Galerie Maya category navigation:', error);
          // Keep the selected filter and native Shopify controls on failure.
        }
      }
      this.render();
    }
    async normalizeSelection() {
      // A language switch can retain a previous locale's text-based filter.
      // Read published locales only when current-locale metadata cannot match it.
      const sourceTexts = [this.config.text];
      const current = new URL(location.href);
      const unresolved = this.filters.some(filter => current.searchParams.getAll(filter.key).some(value =>
        !filter.values.some(item => item.value === value) && !category(filter.key, value, this.config.text)
      ));
      if (unresolved) {
        const languages = (this.config.languages || []).filter(language => language.code !== this.config.locale);
        await Promise.all(languages.map(async language => {
          const url = new URL(language.root.replace(/\/$/, '') + '/collections/all', location.origin);
          url.searchParams.set('section_id', 'collection--body');
          ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
            if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
          });
          try {
            if (!catalogs.has(url.href)) catalogs.set(url.href, fetch(url.href, { credentials: 'same-origin' }).then(async response => {
              if (!response.ok) throw new Error('Source locale unavailable');
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              const data = html.querySelector('[data-maya-config]');
              if (!data) throw new Error('Source locale catalog missing');
              return readConfig(data);
            }));
            const source = await catalogs.get(url.href);
            sourceTexts.push(source.text);
          } catch (_) { catalogs.delete(url.href); }
        }));
      }
      if (!this.isConnected) return false;
      const url = new URL(location.href);
      let changed = false;
      this.filters.forEach(filter => {
        const selected = url.searchParams.getAll(filter.key);
        if (!selected.length) return;
        const converted = selected.map(value => {
          if (filter.values.some(item => item.value === value)) return value;
          const id = sourceTexts.map(text => category(filter.key, value, text)).find(Boolean);
          const match = id && filter.values.find(item => category(filter.key, item.label, this.config.text) === id || category(filter.key, item.value, this.config.text) === id);
          if (match && match.value !== value) { changed = true; return match.value; }
          return value;
        });
        url.searchParams.delete(filter.key);
        converted.forEach(value => url.searchParams.append(filter.key, value));
      });
      if (changed) {
        url.searchParams.delete('page');
      url.searchParams.delete('maya_dimension_page');
        location.replace(url.href);
      }
      return changed;
    }
    linkFor(filter, item) {
      const current = new URL(location.href);
      // Shopify supplies the locale path and actual query value, including GIDs.
      const url = new URL(item ? item.url : this.config.collectionUrl, location.origin);
      url.search = current.search;
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.delete('page');
      url.searchParams.delete('maya_dimension_page');
      if (item) {
        url.searchParams.delete(filter.key);
        url.searchParams.set(filter.key, item.value);
      } else {
        url.searchParams.delete(STYLE);
        url.searchParams.delete(SIZE);
      }
      return url.href;
    }
    render() {
      if (!this.isConnected) return;
      const params = new URLSearchParams(location.search);
      const text = this.config.text;
      const nav = this.querySelector('[data-maya-nav]');
      const heading = this.querySelector('[data-maya-heading]');
      const activeFilter = this.filters.find(filter => params.has(filter.key));
      const selected = activeFilter?.values.filter(item => params.getAll(activeFilter.key).includes(item.value)) || [];
      const activeItem = selected.length === 1 ? selected[0] : null;
      const activeId = activeItem && (category(activeFilter.key, activeItem.label, text) || category(activeFilter.key, activeItem.value, text));
      heading.textContent = activeId ? text[activeId + '_heading'] : (activeItem ? activeItem.label : text.all_rugs);
      const range = this.querySelector('[data-maya-range]');
      const bounds = activeFilter?.key === SIZE && activeItem?.label.match(/\s[-—]\s(.+)$/);
      range.textContent = bounds ? bounds[1] : '';
      range.hidden = !bounds;
      nav.replaceChildren();
      nav.setAttribute('aria-label', activeFilter ? text[activeFilter.key === STYLE ? 'styles' : 'sizes'] : text.browse);
      const add = (label, url, active) => {
        const link = document.createElement('a');
        link.textContent = label;
        link.href = url;
        if (active) link.setAttribute('aria-current', 'page');
        nav.appendChild(link);
      };
      add(text.all, this.linkFor(), !activeFilter);
      (activeFilter ? [activeFilter] : this.filters).forEach(filter => {
        if (!activeFilter) {
          const label = document.createElement('span');
          label.className = 'rug-category-label';
          label.textContent = text[filter.key === STYLE ? 'style' : 'size'];
          nav.appendChild(label);
        }
        const order = definitions[filter.key] || [];
        const idFor = item => category(filter.key, item.label, this.config.text) || category(filter.key, item.value, this.config.text);
        const rank = item => { const n = order.indexOf(idFor(item)); return n < 0 ? order.length : n; };
        [...filter.values].sort((a, b) => rank(a) - rank(b)).forEach(item => {
          const id = idFor(item);
          add(id ? text[id] : item.label, this.linkFor(filter, item), params.getAll(filter.key).includes(item.value));
        });
      });
      this.hidden = false;
    }
  }
  customElements.define('maya-rug-categories', MayaRugCategories);
})();
